import { useState, useEffect } from 'react';
import { Box, Typography, Card, Alert } from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { getApiErrorInfo } from '../../services/api';
import billingService, { type Plan } from '../../services/billingService';
import { track } from '../../services/analytics';
import PlanSelector from '../../components/billing/PlanSelector';
import HubSpotFormEmbed from '../../components/billing/HubSpotFormEmbed';
import CpaxLogo from '../../components/CpaxLogo';
import { trackAdsConversion } from '../../services/googleAds';

export interface BusinessPlanSelectionPanelProps {
  onContinue: (
    basePlanId: string,
    addonIds: string[],
    billingPeriod: 'monthly' | 'annual',
    personCount: number,
    selectedAddons: Plan[],
    addonQuantities: Record<string, number>
  ) => Promise<void>;
}

/**
 * Self-contained business/enterprise plan selector. Fetches the enterprise base plan
 * (auto-included) and enterprise add-ons (user-selected), manages person-count state,
 * and calls onContinue when the user proceeds.
 * Has no page-layout wrapper — embed it anywhere.
 */
export function BusinessPlanSelectionPanel({ onContinue }: BusinessPlanSelectionPanelProps) {
  const [basePlans, setBasePlans] = useState<Plan[]>([]);
  const [addons, setAddons] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [selectedAddonIds, setSelectedAddonIds] = useState<string[]>([]);
  const [addonQuantities, setAddonQuantities] = useState<Record<string, number>>({});
  const [personCount] = useState(1);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setPlansLoading(true);
      setPlansError(null);
      const [allPlans, tenantAddons] = await Promise.all([
        billingService.getAvailablePlans(),
        billingService.getAvailableTenantAddons(),
      ]);
      setBasePlans(allPlans);
      setAddons(tenantAddons);
      // Reset checkout loading state when plans load (fixes stuck button when returning from Chargebee)
      setCheckoutLoading(false);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      let msg: string;
      if (apiError.status === 403) {
        msg = 'Access denied loading plans. Ensure your account has the required role.';
      } else if (apiError.status === 401) {
        msg = 'Authentication required. Please sign in again.';
      } else if (apiError.isNetworkError) {
        msg = 'Unable to connect to the service. Please try again.';
      } else {
        msg = apiError.message || 'Failed to load plans. Please try again.';
      }
      setPlansError(msg);
      setBasePlans([]);
      setAddons([]);
    } finally {
      setPlansLoading(false);
    }
  };

  // Always annual — find yearly base plan
  const basePlan = basePlans.find(p => p.period === 'year');

  const handleToggle = (addonId: string) => {
    const isSelected = selectedAddonIds.includes(addonId);
    if (isSelected) {
      setSelectedAddonIds(prev => prev.filter(id => id !== addonId));
      setAddonQuantities(prev => { const next = { ...prev }; delete next[addonId]; return next; });
    } else {
      setSelectedAddonIds(prev => [...prev, addonId]);
      setAddonQuantities(prev => ({ ...prev, [addonId]: personCount }));
    }
  };

  const handleContinue = async () => {
    if (!basePlan) return;
    const annualAddons = addons.filter(p => p.period === 'year');
    const selectedAddons = selectedAddonIds
      .map(id => annualAddons.find(p => p.id === id))
      .filter(Boolean) as Plan[];
    try {
      setCheckoutLoading(true);
      setCheckoutError(null);
      await onContinue(basePlan.id, selectedAddonIds, 'annual', personCount, selectedAddons, addonQuantities);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setCheckoutError(apiError.message || 'Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <>
      {checkoutError && (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setCheckoutError(null)}>
          {checkoutError}
        </Alert>
      )}

      <PlanSelector
        plans={addons}
        plansLoading={plansLoading}
        plansError={plansError}
        onRetry={loadPlans}
        selectedIds={selectedAddonIds}
        billingPeriod="annual"
        onToggle={handleToggle}
        formatPrice={(plan) => `$${plan.price.toFixed(2)}/user/yr`}
        priceMultiplier={personCount}
        totalFooter={
          <Typography variant="caption" color="text.secondary">
            per year · billed annually
          </Typography>
        }
        continueLabel={
          selectedAddonIds.length > 0
            ? `Continue with ${selectedAddonIds.length} module${selectedAddonIds.length !== 1 ? 's' : ''}`
            : 'Continue with base plan'
        }
        addonQuantities={addonQuantities}
        onQuantityChange={(addonId, val) => setAddonQuantities(prev => ({ ...prev, [addonId]: val }))}
        onContinue={handleContinue}
        continueLoading={checkoutLoading}
        alwaysShowContinue={false}
      />
    </>
  );
}

const HUBSPOT_PORTAL_ID = '51214607';
const HUBSPOT_FORM_ID = '0a27262e-54ab-4cb1-bf22-b21618170e55';
const HUBSPOT_REGION = 'na1';

export default function BusinessPlansPage() {
  useEffect(() => {
    track('plan_viewed', { plan_type: 'business' });
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'surface.subtle' }}>
      {/* Hero header */}
      <Box
        sx={{
          background: 'linear-gradient(160deg, #0c200f 0%, #1a4328 55%, #0d3d1c 100%)',
          pt: { xs: 6, md: 8 },
          pb: { xs: 10, md: 12 },
          px: 4,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle grid overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.06,
            backgroundImage: `
              linear-gradient(rgba(86,151,55,0.5) 1px, transparent 1px),
              linear-gradient(90deg, rgba(86,151,55,0.5) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            pointerEvents: 'none',
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
            <CpaxLogo height={42} variant="light" />
          </Box>
          <Typography
            variant="h3"
            sx={{
              color: 'white',
              fontWeight: 700,
              mb: 1.5,
              fontSize: { xs: 26, md: 34 },
              letterSpacing: '-0.5px',
            }}
          >
            Talk to Our Sales Team
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <SupportAgentIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.5)' }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>
              We'll help you find the right plan for your team
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Sales form card */}
      <Box sx={{ maxWidth: 620, mx: 'auto', px: { xs: 2, sm: 3 }, mt: { xs: 4, sm: 6 }, pb: 8 }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
            p: { xs: 3, sm: 4 },
            bgcolor: 'white',
          }}
        >
          <HubSpotFormEmbed
            portalId={HUBSPOT_PORTAL_ID}
            formId={HUBSPOT_FORM_ID}
            region={HUBSPOT_REGION}
            onSubmitted={() => trackAdsConversion('firm_sales_inquiry')}
          />
        </Card>
      </Box>
    </Box>
  );
}
